import { useEffect, useRef, useState } from 'react';
import { markdown, markdownLanguage } from '@codemirror/lang-markdown';
import { EditorState } from '@codemirror/state';
import { EditorView, keymap, placeholder } from '@codemirror/view';
import { defaultKeymap } from '@codemirror/commands';
import { yCollab, yUndoManagerKeymap } from 'y-codemirror.next';
import { acquire, release, type NoteDoc } from '../data/docs.ts';
import { autocompletion } from '@codemirror/autocomplete';
import { databaseBlocks, liveMarkdown, liveMarkdownTheme, linkClicks } from './liveMarkdown.ts';
import { slashSource, tagSource, wikilinkSource } from './complete.ts';
import { attachmentInput, insertFiles, noteIdFacet } from './attachments.ts';

type Props = { noteId: string };

/**
 * Der Editor arbeitet direkt auf dem Y.Text der Notiz. Damit gibt es keinen Rückweg
 * über den Speicher mehr, der beim Tippen Zeichen verschlucken könnte, und fremde
 * Änderungen erscheinen an der richtigen Stelle, ohne den Cursor zu verlieren.
 */
export function Editor({ noteId }: Props): JSX.Element {
  const host = useRef<HTMLDivElement>(null);
  const [entry, setEntry] = useState<NoteDoc | null>(null);

  useEffect(() => {
    let alive = true;
    void acquire(noteId).then((note) => {
      if (alive) setEntry(note);
      else release(noteId);
    });
    return () => {
      alive = false;
      setEntry(null);
      release(noteId);
    };
  }, [noteId]);

  useEffect(() => {
    if (!entry || !host.current || entry.id !== noteId) return undefined;
    const view = new EditorView({
      parent: host.current,
      state: EditorState.create({
        doc: entry.text.toString(),
        extensions: [
          keymap.of([...yUndoManagerKeymap, ...defaultKeymap]),
          markdown({ base: markdownLanguage }),
          yCollab(entry.text, null),
          autocompletion({
            override: [wikilinkSource, tagSource, slashSource],
            icons: false,
            activateOnTyping: true,
          }),
          noteIdFacet.of(noteId),
          attachmentInput,
          liveMarkdown,
          databaseBlocks,
          linkClicks,
          liveMarkdownTheme,
          EditorView.lineWrapping,
          placeholder('Schreiben …'),
        ],
      }),
    });
    view.focus();
    return () => view.destroy();
  }, [entry, noteId]);

  return <div className="editor" ref={host} />;
}

/** Öffnet den Dateidialog und hängt die Auswahl an die offene Notiz. */
export function useAttachPicker(): () => void {
  return () => {
    const element = document.querySelector('.editor .cm-content');
    const view = element ? EditorView.findFromDOM(element as HTMLElement) : null;
    if (!view) return;
    const picker = document.createElement('input');
    picker.type = 'file';
    picker.multiple = true;
    picker.accept = 'image/*,application/pdf,text/*';
    picker.addEventListener('change', () => {
      void insertFiles(view, [...(picker.files ?? [])]);
    });
    picker.click();
  };
}

/** Fügt Text an der Cursorposition ein — genutzt von der Leiste über der Tastatur. */
export function useEditorInsert(): (text: string) => void {
  return (text: string) => {
    const element = document.querySelector('.editor .cm-content');
    const view = element ? EditorView.findFromDOM(element as HTMLElement) : null;
    if (!view) return;
    const { from, to } = view.state.selection.main;
    view.dispatch({
      changes: { from, to, insert: text },
      selection: { anchor: from + text.length },
    });
    view.focus();
  };
}
